import { legacyToursSuppressed, watchLegacyTourCancellation } from './legacyTourOwnership';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getTourDefinition } from './tourDefinitions';
import { isTourSeen, markTourSeen } from './tourSeen';

/**
 * Risolve `step.element` scegliendo il primo nodo VISIBILE fra tutti i match del
 * selettore, non il primo in ordine di documento.
 *
 * Perche' serve: pagine come `LeadDetailsPage` montano SEMPRE sia il layout mobile
 * (`LeadMobileLayout`, `lg:hidden`) sia quello desktop (`LeadDesktopLayout`,
 * `hidden lg:flex`) — la scelta la fa il CSS, non React. Le ancore `data-tour`
 * esistono quindi DUE volte nel DOM e `document.querySelector` (quello che usa
 * driver.js internamente) restituisce la copia mobile, che su desktop e'
 * `display:none`: driver.js finirebbe per evidenziare un rettangolo 0x0.
 *
 * `getClientRects().length > 0` e' il test di visibilita': e' 0 per gli elementi con
 * `display:none` (anche ereditato da un antenato) e non forza layout aggiuntivo
 * rispetto a `getBoundingClientRect`.
 *
 * @returns il selettore stringa se il primo match e' gia' quello visibile (driver.js
 *   risolvera' lo stesso nodo), l'`Element` visibile se e' un match successivo
 *   (`DriveStep.element` accetta `string | Element | (() => Element)`), il valore
 *   originale se non e' una stringa, `null` se nessun match e' visibile — in questo
 *   caso lo step va scartato, come gia' avveniva per gli step non montati.
 */
function resolveVisibleStepElement(element: DriveStep['element']): DriveStep['element'] | null {
  if (typeof element !== 'string') return element;

  const matches = document.querySelectorAll(element);
  for (let i = 0; i < matches.length; i++) {
    const node = matches[i];
    if (node.getClientRects().length > 0) {
      return i === 0 ? element : node;
    }
  }
  return null;
}

/**
 * true se l'elemento e' nel viewport ma qualcos'altro gli sta sopra (drawer, modale,
 * editor dell'offerta portalato in `layout-root`): il punto centrale non appartiene
 * all'elemento. Fuori viewport non si puo' dire (driver.js ci scrollera') → false.
 */
function isElementCovered(element: DriveStep['element']): boolean {
  const node = typeof element === 'string' ? document.querySelector(element) : element;
  if (!(node instanceof Element)) return false;

  const rect = node.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;

  const top = document.elementFromPoint(x, y);
  return !!top && !node.contains(top);
}

/**
 * Lancia un tour driver.js "inline": invocato direttamente da un componente
 * (es. alla prima apertura di un pannello/drawer ridisegnato), SENZA passare
 * dal query param `?tour=` — quel meccanismo (`useDriverTour` +
 * `useContextualTourTrigger`) e' riservato ai task della checklist di
 * onboarding ed e' pensato per pagine intere, non per elementi che vivono
 * dentro un drawer/modale.
 *
 * Stessa infrastruttura e stesse regole di `useDriverTour.ts`:
 * - se il tour e' gia' stato visto (`isTourSeen`) non fa nulla;
 * - se un altro tour e' gia' in corso non fa nulla e NON marca visto (vedi la
 *   guardia `driver-active` sotto);
 * - filtra gli step il cui elemento (selettore stringa) non e' montato nel
 *   DOM o non e' visibile, per non mostrare un popover fluttuante senza
 *   highlight ne' un highlight 0x0 su una copia nascosta (vedi
 *   `resolveVisibleStepElement`);
 * - se NESSUNO step e' montato, abbandona SENZA marcare "visto" — cosi' il
 *   tour viene ritentato alla prossima apertura utile (stessa regola
 *   anti-bruciatura di `tourSeen.ts`);
 * - il flag "visto" viene scritto appena il tour e' stato davvero mostrato
 *   (subito dopo `drive()`), non piu' solo in `onDestroyed`: se il tour veniva
 *   interrotto senza chiuderlo (pagina ricaricata, scheda chiusa, cambio pagina)
 *   il flag non veniva mai scritto e il fumetto ricompariva a ogni apertura
 *   (segnalazione 29/09/2026). Resta vero che un tour che non parte non marca.
 *
 * Riusata dal drawer condiviso Nota/Attivita (`drawer-redesign`) e, in
 * seguito, dalla cronologia (`cronologia-redesign`).
 *
 * @param opts.onFinished Chiamata alla chiusura/completamento effettivo del tour
 *   (dopo `markTourSeen`), MAI se il tour non e' partito. Usata dal chiamante per
 *   annullare eventuali side-effect di presentazione fatti solo per mostrare il
 *   tour (es. cambio tab temporaneo — fix round 1, vedi ActivityNoteDrawer).
 * @returns true se il tour e' stato effettivamente lanciato (almeno uno step
 *   live), false se non e' partito (gia' visto, tour concorrente gia' attivo,
 *   definizione assente, zero step montati e visibili, primo step coperto da un
 *   pannello aperto sopra) — il chiamante puo' cosi'
 *   distinguere "non partito" da "lanciato" e disfare subito eventuali
 *   side-effect di preparazione.
 */
export function launchInlineTour(tourId: string, opts?: { onFinished?: () => void }): boolean {
  if (legacyToursSuppressed() || isTourSeen(tourId)) return false;

  // Mutua esclusione fra tour. driver.js 1.4.0 tiene config e stato in variabili di
  // MODULO, non nell'istanza: `driver(opts)` chiama subito `F(opts)`, che RIASSEGNA
  // l'oggetto di config globale (dist/driver.js.mjs:1 `let z = {}, J;`, :2-19
  // `function F`, :482-483 `function Ae(e = {}) { F(e); ... }`), e `le(y)` sostituisce
  // pure il riferimento all'istanza globale (:673). Creare un secondo driver mentre il
  // primo e' attivo ne cancellerebbe quindi steps e `onDestroyed`: il primo tour
  // perderebbe sia `markTourSeen` sia l'`onFinished` del chiamante (il ripristino del
  // tab del drawer — l'utente resterebbe sul tab Attivita' con "Salva attivita'" al
  // posto di "Salva nota"). Il segnale di "tour attivo" leggibile senza avere in mano
  // l'istanza e' la classe `driver-active` che driver.js aggiunge al body all'init
  // (:554) e rimuove in destroy (:606) — la stessa condizione che alimenta `isActive()`
  // (:621, `l("isInitialized")`). Nessun `markTourSeen` qui: il tour va ritentato.
  if (document.body.classList.contains('driver-active')) return false;

  const definition = getTourDefinition(tourId);
  if (!definition || definition.steps.length === 0) return false;

  const liveSteps = definition.steps
    .map((s) => {
      const element = resolveVisibleStepElement(s.element);
      return element === null ? null : ({ ...s, element } as DriveStep);
    })
    .filter((s): s is DriveStep => s !== null);
  if (liveSteps.length === 0) return false;

  // Primo step coperto da un pannello aperto sopra la pagina: il popover finirebbe sopra
  // l'altro pannello con l'highlight sul contenuto sbagliato. Non parte e NON marca visto,
  // cosi' viene ritentato alla prossima occasione utile.
  if (isElementCovered(liveSteps[0].element)) return false;

  let interrupted=false;
  let stopWatching=()=>{};
  const instance = driver({
    showProgress: liveSteps.length > 1,
    allowClose: true,
    overlayColor: 'rgba(15, 23, 42, 0.55)',
    stagePadding: 4,
    stageRadius: 8,
    smoothScroll: true,
    nextBtnText: 'Avanti',
    prevBtnText: 'Indietro',
    doneBtnText: 'Fatto',
    progressText: '{{current}} di {{total}}',
    popoverClass: 'flusia-tour',
    steps: liveSteps,
    onDestroyed: () => {
      stopWatching();
      if (!interrupted) markTourSeen(tourId);
      opts?.onFinished?.();
    },
  });

  stopWatching=watchLegacyTourCancellation(()=>{interrupted=true;instance.destroy();});
  if(interrupted)return false;
  instance.drive();
  // Mostrato: da qui vale come visto, anche se non arriva mai a onDestroyed.
  if(!interrupted)markTourSeen(tourId);
  return !interrupted;
}
